import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModelChipRow } from '../shared-camera/components/ModelChipRow';
import { useCamera } from '../shared-camera/hooks/useCamera';
import { loadModels } from '../shared-camera/lib/loadModels';
import { patchOperatorTruth } from '../shared-camera/lib/submitInference';
import type { ModelDescriptor } from '../shared-camera/types';
import { PromptVersionPicker, readStoredPromptVersion } from '../prompts/PromptVersionPicker';
import { ActiveFormatPicker } from './components/ActiveFormatPicker';
import { LivePreview } from './components/LivePreview';
import { PriorSweepResumePrompt } from './components/PriorSweepResumePrompt';
import { ResultsSheet } from './components/ResultsSheet';
import { useActiveFormat } from './hooks/useActiveFormat';
import { useFpsPair } from './hooks/useFpsPair';
import { useFrameSampler } from './hooks/useFrameSampler';
import type { SampledFrame } from './hooks/useFrameSampler';
import {
  computeBinCrop,
  DEFAULT_EXPANSION_FACTOR,
  DEFAULT_MIN_SIDE_PX,
  extractCenterTile,
  projectBBox,
  projectTileBBox,
  type Rect,
} from './lib/cropMath';
import { createBeepPlayer, type BeepPlayer } from './lib/beepPlayer';
import { decodeActiveFormat } from './lib/decodeActiveFormat';
import { createDetectionBeeper, type DetectionBeeper } from './lib/detectionBeeper';
import { selectFreshSymbolsForDispatch } from './lib/dispatchSelection';
import { decoderReady, decodeFrame } from './lib/qrDecoder';
import { uploadScanFrame } from './lib/scanUpload';
import { readResumableSession, SessionStore, type SessionSnapshot } from './lib/sessionStore';
import type { DecodedSymbol, ScanMachineState, ScanResultEntry } from './types';

const DEFAULT_MODEL_KEY = 'anthropic/claude-opus-5';
const MODEL_STORAGE_KEY = 'petf:continuous-scan:model';
const LEGACY_MODEL_SELECTION_STORAGE_KEY = 'petf:continuous-scan:models';
const CROP_ENABLED_STORAGE_KEY = 'petf:continuous-scan:crop-enabled';
const PROMPT_VERSION_STORAGE_KEY = 'petf:continuous-scan:prompt-version';
const COUNT_PROMPT_VERSION_STORAGE_KEY = 'petf:continuous-scan:count-prompt-version';
const RATE_LIMIT_BACKOFF_MS = 2000;

export function ContinuousScanPage() {
  const { videoRef, status: cameraStatus, error: cameraError, start, torchSupported, torchOn, toggleTorch } = useCamera();
  const { display: fps, recordProcessed: recordProcessedFps } = useFpsPair(
    videoRef,
    cameraStatus === 'ready'
  );
  const [models, setModels] = useState<ModelDescriptor[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>(readInitialStoredKey);
  const [promptVersion, setPromptVersion] = useState<string | undefined>(
    () => readStoredPromptVersion(PROMPT_VERSION_STORAGE_KEY)
  );
  const [countPromptVersion, setCountPromptVersion] = useState<string | undefined>(
    () => readStoredPromptVersion(COUNT_PROMPT_VERSION_STORAGE_KEY)
  );
  const [cropEnabled, setCropEnabled] = useState<boolean>(readInitialCropEnabled);
  // Mirror cropEnabled into a ref so the long-lived handleFrame callback
  // sees toggle changes without remounting the rVFC loop.
  const cropEnabledRef = useRef<boolean>(cropEnabled);
  useEffect(() => {
    cropEnabledRef.current = cropEnabled;
    try {
      localStorage.setItem(CROP_ENABLED_STORAGE_KEY, cropEnabled ? 'true' : 'false');
    } catch {
      /* private mode etc. */
    }
  }, [cropEnabled]);
  // Active-format picker. State + ref + localStorage all kept in sync so
  // mid-session swaps (operator changes detector while the rVFC loop is
  // running) take effect on the next frame without remounting the loop.
  // Mobile testing needs the picker in the page UI — DevTools localStorage
  // editing isn't viable on a phone. Phase 2 expands the option list to
  // ArUco dictionaries once fiducialDecoder learns them.
  const {
    activeFormat,
    setActiveFormat,
    activeFormatRef,
    textPattern,
    setTextPattern,
    parsedTextPatternRef,
    fiducialStatus,
    tesseractStatus,
  } = useActiveFormat((t) => setToast(t));
  // Auto fps fallback derived from active format. QR is fastest (200 ms),
  // ArUco needs a bit more (333 ms). Tesseract is heaviest — 500 ms
  // (2 fps) keeps the decode from queueing behind slower phones.
  const frameIntervalMs =
    activeFormat === 'qr' ? 200 : activeFormat === 'text' ? 500 : 333;
  const [state, setState] = useState<ScanMachineState>('priming');
  const [decoderState, setDecoderState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [resumeSnapshot, setResumeSnapshot] = useState<SessionSnapshot | null>(() => {
    if (typeof window === 'undefined') return null;
    const snapshot = readResumableSession();
    return snapshot && snapshot.dedupe.size > 0 ? snapshot : null;
  });
  const [results, setResults] = useState<ScanResultEntry[]>([]);
  const [lastDecoded, setLastDecoded] = useState<DecodedSymbol[]>([]);
  const [decodeFrameSize, setDecodeFrameSize] = useState<{ width: number; height: number } | null>(null);
  const [toast, setToast] = useState<{ kind: 'info' | 'error'; message: string } | null>(null);
  // N of QRs that dispatched together in the most recent multi-symbol
  // frame. Drives the LivePreview "Captured N at once" chip + bbox pulse
  // so the operator sees that the dispatch was parallel, not sequential.
  // Auto-cleared after BURST_DISPLAY_MS via a ref-tracked timer below.
  const [burstCount, setBurstCount] = useState<number>(0);
  const burstTimerRef = useRef<number | null>(null);
  const BURST_DISPLAY_MS = 800;

  // Refs hold the values the long-lived rVFC + decode callback touches.
  // useState would close over the initial render's reference and miss
  // subsequent updates (stale-closure class of bug).
  const sessionRef = useRef<SessionStore>(SessionStore.create());
  const abortMapRef = useRef<Map<string, AbortController>>(new Map());
  const lastBackoffAtRef = useRef<number>(0);
  // Flipped false in the unmount cleanup. Aborted uploads resolve via
  // `applyUploadResult` after unmount; without this guard we'd setState
  // on a dead component AND race the next mount's resume-snapshot read
  // with a trailing persist() write.
  const aliveRef = useRef<boolean>(true);
  // True while a decodeFrame() promise is in flight. The rVFC throttle
  // spaces emissions at 1 fps but not completions — on a slow phone two
  // frames can interleave and both see hasDecoded === false for the same
  // QR. This ref drops the second frame until the first resolves.
  const decodingRef = useRef<boolean>(false);
  // Pending 429-release timeout id. Cleared on each new 429 so back-to-
  // back 429s extend (rather than truncate) the backoff window; cleared
  // in the unmount effect so it can't fire after the component is gone.
  const backoffTimerRef = useRef<number | null>(null);
  // Page-wide cap on simultaneous in-flight uploads. Incremented just
  // before dispatchUploadWithBlob fires; decremented in applyUploadResult
  // (the single settle funnel — handles ok, aborted, error, rate_limited).
  // Reset in the aliveRef setup so a StrictMode dev cycle doesn't leave
  // the counter pinned at the pre-cleanup value.
  const inflightCountRef = useRef<number>(0);
  const MAX_INFLIGHT = 3;
  // ROI fallback (Phase 3): on consecutive full-frame zero-hits, run a
  // second decode pass on a native-resolution center tile. Off by
  // default; flip via `localStorage.setItem('petf:continuous-scan:roi-fallback', 'true')`.
  const roiFallbackEnabledRef = useRef<boolean>(false);
  const consecutiveZeroHitsRef = useRef<number>(0);
  const ROI_FALLBACK_FLAG_KEY = 'petf:continuous-scan:roi-fallback';
  const ROI_FALLBACK_MIN_CONSECUTIVE_MISSES = 2;
  // Per-value sliding-window dedup + Web Audio beep for detection
  // confirmation. Instantiated in the mount effect below (createBeepPlayer
  // registers a document-level unlock listener; running that on every
  // render would leak listeners and audio contexts).
  const beeperRef = useRef<DetectionBeeper | null>(null);
  const beepPlayerRef = useRef<BeepPlayer | null>(null);

  // Auto-activate the camera on mount, matching CapturePage. The page is
  // a focused tool — entering it is the operator's "begin" signal.
  useEffect(() => {
    void start();
  }, [start]);

  // Boot: load models + wait on decoder + check for resumable session.
  useEffect(() => {
    localStorage.removeItem(LEGACY_MODEL_SELECTION_STORAGE_KEY);
    let alive = true;
    decoderReady
      .then(() => alive && setDecoderState('ready'))
      .catch(() => alive && setDecoderState('error'));

    loadModels()
      .then((descriptors) => {
        if (!alive) return;
        setModels(descriptors);
        setSelectedModel((current) => resolveModel(descriptors, current));
      })
      .catch(() => {
        if (alive) setToast({ kind: 'error', message: 'Could not load models. Check API connectivity.' });
      });

    return () => {
      alive = false;
    };
  }, []);

  // Component-unmount cleanup: abort every in-flight POST. Without this,
  // routing away mid-sweep leaks AbortControllers and bills the server
  // for inferences whose results we'll throw away.
  //
  // The `aliveRef.current = true` line in the setup body is load-bearing:
  // React StrictMode (dev) runs setup → cleanup → setup synchronously on
  // mount. Without the re-set in the second setup, aliveRef stays false
  // (cleanup wrote it) for the lifetime of the otherwise-still-mounted
  // component, and every subsequent upload's applyUploadResult bails
  // silently. The map.forEach abort below is a no-op during the StrictMode
  // dance (no POSTs in flight yet); it only matters on real unmount.
  useEffect(() => {
    aliveRef.current = true;
    // StrictMode reset: dev runs setup → cleanup → setup synchronously
    // on mount. Without re-zeroing these in the second setup, they would
    // carry whatever value the (sham) cleanup window observed.
    inflightCountRef.current = 0;
    consecutiveZeroHitsRef.current = 0;
    beeperRef.current = createDetectionBeeper();
    beepPlayerRef.current = createBeepPlayer();
    const map = abortMapRef.current;
    return () => {
      aliveRef.current = false;
      map.forEach((c) => c.abort());
      map.clear();
      if (backoffTimerRef.current !== null) {
        window.clearTimeout(backoffTimerRef.current);
        backoffTimerRef.current = null;
      }
      if (burstTimerRef.current !== null) {
        window.clearTimeout(burstTimerRef.current);
        burstTimerRef.current = null;
      }
      beepPlayerRef.current?.dispose();
      beepPlayerRef.current = null;
      beeperRef.current = null;
    };
  }, []);

  // ROI fallback flag. Read once at mount, then again on every `storage`
  // event so a cross-tab toggle takes effect without reload. We don't
  // re-read per-frame: that would burn synchronous localStorage on the
  // rVFC tick for no good reason.
  useEffect(() => {
    const readFlag = () => {
      try {
        roiFallbackEnabledRef.current =
          localStorage.getItem(ROI_FALLBACK_FLAG_KEY) === 'true';
      } catch {
        roiFallbackEnabledRef.current = false;
      }
    };
    readFlag();
    window.addEventListener('storage', readFlag);
    return () => window.removeEventListener('storage', readFlag);
  }, []);

  useEffect(() => {
    if (!selectedModel) return;
    localStorage.setItem(MODEL_STORAGE_KEY, selectedModel);
  }, [selectedModel]);

  const selectModel = useCallback((key: string) => {
    setSelectedModel((prev) => (prev === key ? prev : key));
  }, []);

  // Pause sampling (but NOT in-flight uploads) when the tab is hidden.
  // Cancelling running POSTs would just bill the server for inferences
  // whose results we'd throw away.
  const [tabVisible, setTabVisible] = useState(
    typeof document === 'undefined' || document.visibilityState !== 'hidden'
  );
  useEffect(() => {
    const handler = () => setTabVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  const applyUploadResult = useCallback(
    (key: string, result: Awaited<ReturnType<typeof uploadScanFrame>>) => {
      // Decrement the concurrency cap counter FIRST so a slot frees
      // even on the post-unmount-abort path (so a remount can
      // dispatch immediately). Clamp to 0 to guard against
      // double-call paths (shouldn't happen, but cheap insurance).
      inflightCountRef.current = Math.max(0, inflightCountRef.current - 1);
      // After unmount, aborted POSTs still resolve here. Drop them — a
      // setResults would warn, and a persist() would race the next
      // mount's resume-snapshot read.
      if (!aliveRef.current) return;
      const session = sessionRef.current;
      if (result.kind === 'ok') {
        session.updateResult(key, { status: 'ok', response: result.response });
      } else if (result.kind === 'aborted') {
        session.updateResult(key, { status: 'aborted' });
      } else if (result.kind === 'rate_limited') {
        const windowMs = result.retryAfterMs ?? RATE_LIMIT_BACKOFF_MS;
        // Backoff window end timestamp — handleFrame's guard reads this
        // ref directly, so back-to-back 429s correctly extend the window.
        lastBackoffAtRef.current = Date.now() + windowMs;
        session.updateResult(key, { status: 'error', errorMessage: 'rate limited — backing off' });
        // Clear any prior pending release so the longest window wins,
        // not the first one to fire.
        if (backoffTimerRef.current !== null) {
          window.clearTimeout(backoffTimerRef.current);
        }
        backoffTimerRef.current = window.setTimeout(() => {
          if (!aliveRef.current) return;
          backoffTimerRef.current = null;
        }, windowMs);
      } else if (result.kind === 'unauthorized') {
        // The shared apiClient response interceptor already redirects to
        // /login on 401 — preserve the dedupe Set en route via the
        // already-persisted session.
        session.updateResult(key, { status: 'error', errorMessage: 'session expired' });
      } else {
        session.updateResult(key, { status: 'error', errorMessage: result.message });
        // Allow re-attempt on next decode: the QR is no longer "decoded".
        const entry = session.results.find((r) => r.idempotencyKey === key);
        if (entry) session.forgetDecoded(entry.qrValue);
      }
      setResults([...session.results]);
      void session.persist();
    },
    []
  );

  // Fire the POST for an already-encoded crop. The IIFE only closes
  // over `blob` and the small per-call refs — *not* the frame, so the
  // caller can release frame.originalImageData as soon as it returns
  // from its tick.
  const dispatchUploadWithBlob = useCallback(
    (
      symbol: DecodedSymbol,
      blob: Blob,
      idempotencyKey: string,
      controller: AbortController,
    ) => {
      void (async () => {
        try {
          const result = await uploadScanFrame({
            imageBlob: blob,
            qrText: symbol.text,
            clientIdempotencyKey: idempotencyKey,
            modelKeys: [selectedModel],
            promptVersion,
            countPromptVersion,
            deviceUserAgent: navigator.userAgent,
            signal: controller.signal,
          });
          applyUploadResult(idempotencyKey, result);
        } catch (err: unknown) {
          applyUploadResult(idempotencyKey, {
            kind: 'error',
            message: (err as Error)?.message ?? 'upload failed',
          });
        } finally {
          abortMapRef.current.delete(idempotencyKey);
        }
      })();
    },
    [selectedModel, promptVersion, countPromptVersion, applyUploadResult]
  );

  const handleFrame = useCallback(
    async (frame: SampledFrame) => {
      if (decoderState !== 'ready') return;
      // Bail if a previous frame's decode is still in flight — otherwise
      // two concurrent decodes can both pass the !hasDecoded check for
      // the same QR and we'd POST twice for one symbol.
      if (decodingRef.current) return;
      decodingRef.current = true;

      // Capture format at decode-start; drop outcome if it changes mid-flight.
      // Otherwise a text hit in flight while the operator switches to QR
      // would dispatch as if it were QR, and the auto-revert-on-failure
      // path would upload phantom text values.
      const formatAtStart = activeFormatRef.current;
      let fullOutcome: Awaited<ReturnType<typeof decodeActiveFormat>> | null = null;
      try {
        fullOutcome = await decodeActiveFormat(frame.imageData, formatAtStart, {
          text: { parsed: parsedTextPatternRef.current },
        });
      } finally {
        // Always record: the HUD's "processed FPS" is frame-handler
        // throughput, not per-detector success rate. Missing this under
        // a wedged decoder leaves the operator staring at `-/-` with no
        // signal that anything's alive.
        recordProcessedFps();
      }
      if (activeFormatRef.current !== formatAtStart) {
        // Format changed mid-decode. Drop the outcome; the new format will
        // fire on the next frame.
        decodingRef.current = false;
        return;
      }

      try {
        // fullOutcome is guaranteed set here (finally ran after assignment,
        // and the format-guard would have early-returned on undefined).
        const outcome = fullOutcome!;
        setDecodeFrameSize({ width: frame.imageData.width, height: frame.imageData.height });

        // Project each detected symbol's bbox into original-frame coords
        // up front, so the rest of the pipeline works in a single coord
        // space. Source depends on which decode pass found it.
        let symbolsInOriginalCoords: DecodedSymbol[] = [];

        if (outcome.kind === 'symbols') {
          consecutiveZeroHitsRef.current = 0;
          setLastDecoded(outcome.symbols); // overlay uses decode-frame coords
          symbolsInOriginalCoords = outcome.symbols.map((s) => ({
            ...s,
            bbox: projectBBox(s.bbox, frame.decodeFrameSize, frame.originalFrameSize),
          }));
        } else {
          consecutiveZeroHitsRef.current++;
          setLastDecoded([]);
          // ROI fallback: native-resolution center-tile decode when
          // the full-frame raster has missed N consecutive frames AND
          // the flag is on. Skips on early-empty frames so the operator
          // can pan over blank wall without burning the decode budget.
          if (
            roiFallbackEnabledRef.current &&
            consecutiveZeroHitsRef.current >= ROI_FALLBACK_MIN_CONSECUTIVE_MISSES
          ) {
            const { tile, origin } = extractCenterTile(frame.originalImageData);
            const roiOutcome = await decodeFrame(tile);
            if (roiOutcome.kind === 'symbols') {
              // Tile-local bboxes -> original-frame coords via
              // projectTileBBox (a simple shift). No projectBBox call
              // because the tile decode ran at native resolution, not
              // a downscaled raster.
              symbolsInOriginalCoords = roiOutcome.symbols.map((s) => ({
                ...s,
                bbox: projectTileBBox(s.bbox, origin),
              }));
              consecutiveZeroHitsRef.current = 0;
            }
          }
        }

        if (symbolsInOriginalCoords.length === 0) return;

        // Detection beep: sliding-window per-value dedup on the RAW
        // decode output (not the fresh-for-dispatch filter, which is
        // permanent-per-session). Fires at most one beep per frame.
        // Guarded by the length check above so the .map() and the
        // observe/prune pass don't run on the ~200 no-detect frames
        // per minute of a typical sweep.
        const beeper = beeperRef.current;
        const player = beepPlayerRef.current;
        if (beeper && player) {
          const fresh = beeper.observe(symbolsInOriginalCoords.map((s) => s.text));
          if (fresh > 0) player.play();
        }

        const session = sessionRef.current;
        // Filter degenerate bboxes + dedupe + sort by bbox area desc.
        // See dispatchSelection.ts — pure function so the regression-
        // catching test lives in __tests__/dispatchSelection.test.ts.
        const ordered = selectFreshSymbolsForDispatch({
          symbols: symbolsInOriginalCoords,
          hasDecoded: (t) => session.hasDecoded(t),
        });
        if (ordered.length === 0) return;

        // Honor the rate-limit backoff window. lastBackoffAtRef is the
        // window's *end* timestamp, set in applyUploadResult.
        if (Date.now() < lastBackoffAtRef.current) {
          return;
        }

        // Reserve idempotency keys + add placeholder result rows + compute
        // crops, all synchronously so pending rows render together.
        // Concurrency cap: stop preparing once the global in-flight
        // counter would exceed MAX_INFLIGHT. Symbols past the cap are
        // NOT markDecoded'd, so the next frame's filter re-considers
        // them once a slot frees.
        interface Prep {
          sym: DecodedSymbol;
          rect: Rect;
          idempotencyKey: string;
          controller: AbortController;
          skip: boolean;
        }
        const prepared: Prep[] = [];
        for (const sym of ordered) {
          if (inflightCountRef.current >= MAX_INFLIGHT) break;
          session.markDecoded(sym.text);
          // Increment immediately so concurrent handleFrame ticks see
          // the load. Decremented in applyUploadResult (single settle
          // funnel for ok, aborted, error, rate_limited, encode-fail).
          inflightCountRef.current++;
          const idempotencyKey = crypto.randomUUID();
          const controller = new AbortController();
          abortMapRef.current.set(idempotencyKey, controller);

          const placeholderBlob = new Blob([], { type: 'image/jpeg' });
          sessionRef.current.addResult({
            idempotencyKey,
            qrValue: sym.text,
            snapshotBlob: placeholderBlob,
            capturedAt: frame.capturedAt,
            status: 'pending',
          });

          // sym.bbox is already in original-frame coords (projected at
          // the decode source — see the projectBBox / projectTileBBox
          // calls above), so computeBinCrop runs without another step.
          // When the operator has disabled bin cropping, send the full
          // frame instead — useful when the bbox-driven crop is trimming
          // off the target bin.
          const rect: Rect = cropEnabledRef.current
            ? computeBinCrop(sym.bbox, frame.originalFrameSize, {
                expansionFactor: DEFAULT_EXPANSION_FACTOR,
                minSidePx: DEFAULT_MIN_SIDE_PX,
              })
            : {
                x: 0,
                y: 0,
                w: frame.originalFrameSize.width,
                h: frame.originalFrameSize.height,
              };
          const skip = rect.w === 0 || rect.h === 0;
          prepared.push({ sym, rect, idempotencyKey, controller, skip });
        }
        if (prepared.length === 0) return;
        setResults([...sessionRef.current.results]);

        // Multi-symbol frame: surface the parallel dispatch via the
        // LivePreview burst chip + bbox pulse. Count the non-skipped
        // entries so a frame that prepared 3 but had one zero-area
        // crop still reads as "Captured 2 at once". Reset the auto-
        // clear timer on each burst so back-to-back multi frames keep
        // the chip visible instead of flickering.
        const dispatchedCount = prepared.filter((p) => !p.skip).length;
        if (dispatchedCount >= 2) {
          setBurstCount(dispatchedCount);
          if (burstTimerRef.current !== null) {
            window.clearTimeout(burstTimerRef.current);
          }
          burstTimerRef.current = window.setTimeout(() => {
            if (!aliveRef.current) return;
            setBurstCount(0);
            burstTimerRef.current = null;
          }, BURST_DISPLAY_MS);
        }

        // Pre-encode all crops WITHIN this tick. Promises start
        // concurrently; awaited sequentially with per-symbol error
        // handling so one encode failure doesn't kill the frame's
        // other dispatches. Critical: by the time this loop returns,
        // every upload's IIFE closes over its own Blob — not over the
        // frame — so frame.originalImageData (~8MB at 1920x1080 RGBA)
        // can be GC'd as soon as handleFrame's tick unwinds.
        const encodePromises = prepared.map((p) =>
          p.skip ? null : frame.encodeJpegCrop(p.rect)
        );
        for (let i = 0; i < prepared.length; i++) {
          const p = prepared[i];
          if (p.skip) {
            applyUploadResult(p.idempotencyKey, {
              kind: 'error',
              message: 'crop rect is zero-area (likely stale frame dimensions)',
            });
            abortMapRef.current.delete(p.idempotencyKey);
            continue;
          }
          let blob: Blob;
          try {
            blob = await encodePromises[i]!;
          } catch (err: unknown) {
            applyUploadResult(p.idempotencyKey, {
              kind: 'error',
              message: (err as Error)?.message ?? 'snapshot encode failed',
            });
            abortMapRef.current.delete(p.idempotencyKey);
            continue;
          }
          if (aliveRef.current) {
            sessionRef.current.updateResult(p.idempotencyKey, { snapshotBlob: blob });
          }
          dispatchUploadWithBlob(p.sym, blob, p.idempotencyKey, p.controller);
        }
        setResults([...sessionRef.current.results]);
      } finally {
        decodingRef.current = false;
      }
    },
    // rateLimited is no longer read here — backoff is gated via the
    // lastBackoffAtRef timestamp set by applyUploadResult.
    [decoderState, dispatchUploadWithBlob, applyUploadResult, recordProcessedFps]
  );

  // Auto-promote priming → scanning once camera + decoder both ready.
  // Camera failure surfaces via derivedState below.
  const derivedState: ScanMachineState = useMemo(() => {
    if (cameraStatus === 'denied' || cameraStatus === 'error') return 'error';
    if (state === 'priming' && cameraStatus === 'ready' && decoderState === 'ready') {
      return 'scanning';
    }
    return state;
  }, [state, cameraStatus, decoderState]);

  // Sync the auto-promotion back to state so pause/resume work cleanly.
  useEffect(() => {
    if (state === 'priming' && derivedState === 'scanning') {
      setState('scanning');
    }
  }, [state, derivedState]);

  useFrameSampler({
    videoRef,
    // videoReady gates effect mount — without it, videoRef.current going
    // null → non-null on the LivePreview mount wouldn't re-run the effect
    // and the rVFC loop would never start.
    videoReady: cameraStatus === 'ready',
    enabled: derivedState === 'scanning' && tabVisible,
    onFrame: handleFrame,
    frameIntervalMs,
  });

  const handlePause = useCallback(() => setState('paused'), []);
  const handleResume = useCallback(() => setState('scanning'), []);
  const handleFinish = useCallback(() => setState('reviewing'), []);
  const handleNewSweep = useCallback(() => {
    sessionRef.current.clearPersisted();
    sessionRef.current = SessionStore.create();
    setResults([]);
    setLastDecoded([]);
    setState('scanning');
  }, []);

  const handleForget = useCallback((idempotencyKey: string) => {
    const session = sessionRef.current;
    const entry = session.results.find((r) => r.idempotencyKey === idempotencyKey);
    if (!entry) return;
    session.forgetDecoded(entry.qrValue);
    session.removeResult(idempotencyKey);
    setResults([...session.results]);
    void session.persist();
  }, []);

  const handleTruth = useCallback((idempotencyKey: string, truth: number) => {
    const session = sessionRef.current;
    const entry = session.results.find((r) => r.idempotencyKey === idempotencyKey);
    if (!entry || !entry.response) return;
    session.updateResult(idempotencyKey, { operatorTruth: truth });
    setResults([...session.results]);
    void session.persist();
    patchOperatorTruth(entry.response.inference_run_id, truth).catch((err) => {
      setToast({ kind: 'error', message: err?.message ?? 'Could not save truth.' });
    });
  }, []);

  const handleResumeSnapshot = useCallback(() => {
    if (!resumeSnapshot) return;
    sessionRef.current = SessionStore.resume(resumeSnapshot);
    setResults([...sessionRef.current.results]);
    setResumeSnapshot(null);
  }, [resumeSnapshot]);

  const handleDiscardSnapshot = useCallback(() => {
    try {
      // Discard the persisted session entirely.
      const id = resumeSnapshot?.sessionId;
      if (id) localStorage.removeItem(`par-excellence-proto_scan_${id}`);
    } catch {
      /* ignore */
    }
    setResumeSnapshot(null);
  }, [resumeSnapshot]);

  return (
    <div className="space-y-4 pb-12" data-testid="continuous-scan-page">
      <header>
        <h1 className="text-2xl font-bold">Continuous scan</h1>
        <p className="text-sm text-gray-400">
          Sweep bins continuously — point, dwell, move on.
        </p>
      </header>

      {resumeSnapshot && (
        <PriorSweepResumePrompt
          snapshot={resumeSnapshot}
          onResume={handleResumeSnapshot}
          onDiscard={handleDiscardSnapshot}
        />
      )}

      <section className="space-y-3">
        <p className="text-sm font-medium text-gray-300">Model</p>
        <ModelChipRow
          models={models}
          selectedKey={selectedModel}
          onSelect={selectModel}
          disabled={derivedState === 'scanning' || derivedState === 'paused'}
        />
        <div className="flex flex-wrap gap-3">
          <PromptVersionPicker
            label="Fullness prompt"
            filter={{ kind: 'bin-fullness' }}
            storageKey={PROMPT_VERSION_STORAGE_KEY}
            value={promptVersion}
            onChange={setPromptVersion}
            disabled={derivedState === 'scanning' || derivedState === 'paused'}
            testId="fullness-prompt-picker"
          />
          <PromptVersionPicker
            label="Count prompt"
            filter={{ kind: 'count' }}
            storageKey={COUNT_PROMPT_VERSION_STORAGE_KEY}
            value={countPromptVersion}
            onChange={setCountPromptVersion}
            disabled={derivedState === 'scanning' || derivedState === 'paused'}
            testId="count-prompt-picker"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-300" data-testid="crop-toggle">
          <input
            type="checkbox"
            checked={cropEnabled}
            onChange={(e) => setCropEnabled(e.target.checked)}
            className="h-4 w-4"
          />
          <span>
            Crop to bin region (QR-anchored)
            <span className="ml-2 text-xs text-gray-500">
              uncheck to send the full frame to the model
            </span>
          </span>
        </label>
        <ActiveFormatPicker
          activeFormat={activeFormat}
          onFormatChange={setActiveFormat}
          fiducialStatus={fiducialStatus}
          tesseractStatus={tesseractStatus}
          textPattern={textPattern}
          onTextPatternChange={setTextPattern}
          selectId="active-format-picker"
        />
      </section>

      {derivedState !== 'reviewing' && (
        <LivePreview
          videoRef={videoRef}
          lastDecodedSymbols={derivedState === 'scanning' ? lastDecoded : []}
          decodeFrameSize={decodeFrameSize}
          burstCount={derivedState === 'scanning' ? burstCount : 0}
          torchSupported={torchSupported}
          torchOn={torchOn}
          onToggleTorch={toggleTorch}
          cameraFps={fps.camera}
          processedFps={fps.processed}
        />
      )}

      <ControlBar
        state={derivedState}
        onPause={handlePause}
        onResume={handleResume}
        onFinish={handleFinish}
        onNewSweep={handleNewSweep}
      />

      {toast && (
        <div
          role={toast.kind === 'error' ? 'alert' : 'status'}
          className={[
            'rounded-md px-4 py-3 text-sm',
            toast.kind === 'error'
              ? 'bg-red-900/40 text-red-100 ring-1 ring-red-700'
              : 'bg-emerald-900/40 text-emerald-100 ring-1 ring-emerald-700',
          ].join(' ')}
          data-testid="scan-toast"
        >
          {toast.message}
        </div>
      )}

      {derivedState === 'error' && cameraError && (
        <p role="alert" className="text-sm text-red-300">{cameraError}</p>
      )}

      <ResultsSheet results={results} onForget={handleForget} onTruth={handleTruth} />
    </div>
  );
}

interface ControlBarProps {
  state: ScanMachineState;
  onPause: () => void;
  onResume: () => void;
  onFinish: () => void;
  onNewSweep: () => void;
}

function ControlBar({ state, onPause, onResume, onFinish, onNewSweep }: ControlBarProps) {
  if (state === 'scanning') {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onPause}
          className="flex-1 rounded-md bg-gray-700 px-4 py-3 text-sm hover:bg-gray-600"
          data-testid="pause-scanning"
        >
          Pause
        </button>
        <button
          type="button"
          onClick={onFinish}
          className="flex-1 rounded-md bg-blue-600 px-4 py-3 text-sm font-medium hover:bg-blue-500"
          data-testid="finish-sweep"
        >
          Finish sweep
        </button>
      </div>
    );
  }
  if (state === 'paused') {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onResume}
          className="flex-1 rounded-md bg-emerald-600 px-4 py-3 text-sm font-medium hover:bg-emerald-500"
          data-testid="resume-scanning"
        >
          Resume
        </button>
        <button
          type="button"
          onClick={onFinish}
          className="flex-1 rounded-md bg-blue-600 px-4 py-3 text-sm font-medium hover:bg-blue-500"
          data-testid="finish-sweep"
        >
          Finish sweep
        </button>
      </div>
    );
  }
  if (state === 'reviewing') {
    return (
      <button
        type="button"
        onClick={onNewSweep}
        className="w-full rounded-md bg-emerald-600 px-4 py-3 text-sm font-medium hover:bg-emerald-500"
        data-testid="new-sweep"
      >
        Start a new sweep
      </button>
    );
  }
  return null;
}

function readInitialStoredKey(): string {
  try {
    return localStorage.getItem(MODEL_STORAGE_KEY) || DEFAULT_MODEL_KEY;
  } catch {
    return DEFAULT_MODEL_KEY;
  }
}

function readInitialCropEnabled(): boolean {
  try {
    // Default ON — current behavior, only OFF when the operator opts out.
    return localStorage.getItem(CROP_ENABLED_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

function resolveModel(descriptors: ModelDescriptor[], current: string): string {
  const enabled = descriptors.filter((d) => d.enabled);
  const candidates = enabled.length > 0 ? enabled : descriptors;
  if (current && candidates.some((d) => d.key === current)) return current;
  if (candidates.some((d) => d.key === DEFAULT_MODEL_KEY)) return DEFAULT_MODEL_KEY;
  return candidates[0]?.key ?? DEFAULT_MODEL_KEY;
}
